import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { Languages } from 'lucide-react';
import { useTranslateTexts } from '../../services/textTranslation.service';
import type { TranslationLocale } from '../../services/textTranslation.service';

interface LocalizedTextFieldsProps {
  label: string;
  helperText?: string;
  valueEn: string;
  valueVi: string;
  onChange: (next: { en: string; vi: string }) => void;
  multiline?: boolean;
  minRows?: number;
  maxLength?: number;
  disabled?: boolean;
}

type FieldKey = 'en' | 'vi';

const localeLabel: Record<FieldKey, string> = {
  en: 'EN',
  vi: 'VI',
};

export const LocalizedTextFields: React.FC<LocalizedTextFieldsProps> = ({
  label,
  helperText,
  valueEn,
  valueVi,
  onChange,
  multiline = false,
  minRows,
  maxLength,
  disabled = false,
}) => {
  const translateMutation = useTranslateTexts();
  const [loadingTarget, setLoadingTarget] = useState<FieldKey | null>(null);
  const lastAutoSourceRef = useRef<Record<FieldKey, string>>({ en: '', vi: '' });

  const values = { en: valueEn, vi: valueVi };
  const isTranslating = translateMutation.isPending || loadingTarget !== null;

  const translate = async (source: FieldKey, overwrite = false) => {
    const target: FieldKey = source === 'en' ? 'vi' : 'en';
    const sourceText = values[source].trim();

    if (!sourceText || disabled || isTranslating || (!overwrite && values[target].trim())) {
      return;
    }

    setLoadingTarget(target);
    try {
      const fields = await translateMutation.mutateAsync({
        sourceLocale: source as TranslationLocale,
        targetLocale: target as TranslationLocale,
        fields: { text: sourceText },
      });
      onChange({
        en: target === 'en' ? (fields.text ?? '') : valueEn,
        vi: target === 'vi' ? (fields.text ?? '') : valueVi,
      });
      lastAutoSourceRef.current[source] = sourceText;
    } catch {
      // The axios interceptor already toasts the server message (quota / translation failed).
    } finally {
      setLoadingTarget(null);
    }
  };

  const handleBlur = (source: FieldKey) => {
    const target: FieldKey = source === 'en' ? 'vi' : 'en';
    const sourceText = values[source].trim();

    if (sourceText && !values[target].trim() && lastAutoSourceRef.current[source] !== sourceText) {
      void translate(source);
    }
  };

  const renderField = (field: FieldKey) => {
    const opposite = field === 'en' ? 'vi' : 'en';
    const sourceEmpty = !values[opposite].trim();
    const buttonDisabled = disabled || sourceEmpty || isTranslating;

    return (
      <Box key={field}>
        <Box className="mb-1 flex items-center justify-between gap-2">
          <Typography variant="caption" className="font-semibold text-gray-600 dark:text-slate-300">
            {label} ({localeLabel[field]})
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={
              loadingTarget === field ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <Languages size={14} />
              )
            }
            disabled={buttonDisabled}
            onClick={() => void translate(opposite, true)}
            sx={{ minWidth: 0, textTransform: 'none', fontSize: 12, px: 0.5 }}
          >
            Translate from {localeLabel[opposite]}
          </Button>
        </Box>
        <TextField
          value={values[field]}
          onChange={(event) => onChange({ ...values, [field]: event.target.value })}
          onBlur={() => handleBlur(field)}
          fullWidth
          multiline={multiline}
          minRows={minRows}
          // Locked while its translation is in flight so the result never overwrites typing.
          disabled={disabled || loadingTarget === field}
          helperText={helperText}
          inputProps={{ maxLength }}
        />
      </Box>
    );
  };

  return (
    <Box className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {renderField('en')}
      {renderField('vi')}
    </Box>
  );
};
